import { Box, Typography, IconButton, Slider } from "@mui/material";
import NavigationRefresh from '@mui/icons-material/Refresh';
import { Bar, BarProps, CartesianGrid, ComposedChart, Legend, Line, LineChart, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import moment from "moment-timezone";


import { dataTagErrorSymbol, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { apiRoutes, fetchJson, useCurrencyRates } from "@/src/services";
import { buildQueryUrl } from "@/src/utils";
import { chain, mapValues } from "lodash";



interface Props {
    timezone: string,
    // days?: number
}

interface StatRecord {
    amount: number,
    count: number
}



type RecordSource = { _id: Date } & Record<string, StatRecord>;

export const DepositsByDay = ({ timezone = "+0000" }: Props) => {
    const { valueIn, isFetched } = useCurrencyRates()
    const [days, setDays] = useState(30);

    const { data, refetch } = useQuery<RecordSource[], Error, ({ _id: Date } & StatRecord)[]>({
        queryKey: ['deposits-by-day', days, timezone],
        queryFn: () => fetchJson(buildQueryUrl(apiRoutes.dashboard.depositsByDay2, { days, timezone }))
            .then(resp => resp.json),

        staleTime: Infinity,
        refetchOnWindowFocus: false,
        select: (source) => source.map(({ _id, ...stats }) => ({
            _id,
            count: chain(stats).mapValues().map(v => v.count).sum().value(),
            amount: chain(stats).mapValues().map((v, currency) => valueIn(v.amount, currency, "USD")).sum().value(),
        })),
        enabled: isFetched
    });



    return (
        <Box>
            <IconButton onClick={() => refetch({ cancelRefetch: true })} aria-label="refresh" color="primary"><NavigationRefresh /></IconButton>
            <Typography variant="h6" align="center">Deposits By Days</Typography>
            <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={data}
                    margin={{ top: 20, right: -20, left: -20, bottom: 0 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="_id"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => (!value || value === "auto") ? value : moment(value).tz(timezone).format('DD MMM')}
                    />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <Tooltip
                        labelFormatter={(value: any) => moment(value).tz(timezone).format('D MMM')}
                        formatter={(value: number, name, item) => {
                            return name === "amount" ? value.toFixed(2) : value;
                        }}
                    />
                    <Legend />

                    <Bar yAxisId="left" dataKey="count" fill="#413ea0" fillOpacity={0.5} stroke="#413ea0" shape={<BoxedBar />} />
                    <Line yAxisId="right" type="monotone" dataKey="amount" stroke="#387908" activeDot={{ r: 8 }} />

                </ComposedChart>

            </ResponsiveContainer>
            <Box px={"15%"}>
                <Slider
                    value={days}
                    onChange={(e, newValue) => setDays(newValue as number)}
                    marks={[{ value: 30, label: "30" }, { value: 3 * 30, label: "90" }, { value: 6 * 30, label: "180" }]}
                    // valueLabelDisplay="on"
                    step={null}
                    min={20} max={200}
                />
            </Box>
        </Box>
    )
};

const BoxedBar = (props: any) => {
    const { fill, x, y, width, height, value } = props;
    const _height = height / value;
    return <>
        {Array(value).fill(1).map((_, v) => {

            const _y = y + (_height * v)
            return <Rectangle key={v} {...props} y={_y} height={_height * 0.95} />
        })}
    </>

}